import { z } from 'zod';
import { imageWithUploadSchema, partySchema } from '../../common';
import { dateSchema, idSchema } from '../../validation';

/**
 * Waybill generation lifecycle.
 *
 * - `generating` — PDF job is in flight (prior `image` may still be present)
 * - `ready` — `image` is current
 * - `failed` — last attempt failed (`error` may explain why)
 *
 * Legacy docs without `status` but with `image` are treated as ready.
 */
export const orderWaybillStatusSchema = z.enum(['generating', 'ready', 'failed']);

export const orderWaybillSchema = z.object({
  status: orderWaybillStatusSchema.optional(),
  /** PDF image of the waybill. Optional while status is `generating` / `failed`. */
  image: imageWithUploadSchema.optional(),
  /** Time the current PDF was generated. */
  generatedAt: dateSchema.optional(),
  /** Last failure message when status is `failed`. */
  error: z.string().optional(),
});

export type OrderWaybillStatus = z.infer<typeof orderWaybillStatusSchema>;
export type OrderWaybill = z.infer<typeof orderWaybillSchema>;

/**
 * Lifecycle of a Bayan waybill as Hero tracks it.
 *
 * Mirrors Bayan's own status list (appendix 6.5) plus the two states that only
 * exist on Hero's side: `pending` (claimed but not sent) and `failed` (Hero
 * tried and Bayan refused).
 */
export const waybillBayanStatusSchema = z.enum([
  'pending',
  'issued',
  'closed',
  'canceled',
  'failed',
]);
export type WaybillBayanStatus = z.infer<typeof waybillBayanStatusSchema>;

/** Where a carrier 70… the issuance tried came from. */
export const waybillTriedMoiSourceSchema = z.enum([
  'istimara_owner',
  'iqama_employer',
  'account',
  'typed',
  'other',
]);
export type WaybillTriedMoiSource = z.infer<typeof waybillTriedMoiSourceSchema>;

export const waybillTriedMoiSchema = z.object({
  moi: z.string(),
  source: waybillTriedMoiSourceSchema,
  /** Printed company name beside the number on the card, or the account's org name. */
  name: z.string().optional(),
});
export type WaybillTriedMoi = z.infer<typeof waybillTriedMoiSchema>;

/**
 * One reason an order cannot be sent to Bayan yet.
 *
 * Produced by the mapper's validation pass and stored on the order so the
 * dashboard and the WhatsApp agent both render the same list without
 * re-running the mapper. `entity` + `entityId` exist so the UI can deep-link
 * straight to the vehicle / driver / region that needs fixing rather than
 * leaving the user to hunt for it.
 *
 * `code` is stable and machine-readable (`missing_driver_issue_number`) so the
 * frontend owns the localized copy; `message` is the English fallback for
 * logs, the admin viewer, and any code that has no translation for a new code.
 */
export const waybillBlockerSchema = z.object({
  code: z.string(),
  message: z.string(),
  entity: z.enum(['order', 'driver', 'vehicle', 'org', 'region', 'item', 'app']),
  /** `_id` hex string of the offending document, when there is a single one. */
  entityId: z.string().optional(),
  /**
   * City display name on `city_unmapped` — the address `city` component,
   * never a neighborhood / district. Lets chat name the city without
   * parsing the English `message`.
   */
  cityName: z.string().optional(),
  /**
   * Bayan good-type branch the answer lives under, on `good_type_required`
   * when Hero placed the load in a branch but not on a leaf. Pickers and
   * chat offer only that branch's leaves instead of the whole catalog.
   */
  goodTypeBranchId: z.number().int().positive().optional(),
  /**
   * Every carrier 70… ELM refused for this filing, in the order tried, on
   * `carrier_moi_not_licensed` / `carrier_not_vehicle_party`. Lets chat
   * list what was already tried (cards + the account's own number) instead
   * of asking for a number the user just sent. Look-alike digit variants
   * are never listed — the user never sent them.
   */
  triedMois: z.array(waybillTriedMoiSchema).optional(),
});
export type WaybillBlocker = z.infer<typeof waybillBlockerSchema>;

/**
 * State of the GOVERNMENT waybill (وثيقة النقل) for this order.
 *
 * ## Why this lives on the order and not in its own collection
 *
 * It is the source of truth, not a cache. Keeping it here means the order
 * detail screen, the order list, and the WhatsApp agent answer "is the وثيقة
 * issued?" from the document they already loaded — no join, no second
 * round-trip on a latency-sensitive path.
 *
 * It also makes the guard against double issuance a single atomic write.
 * Both a broker and its subcontracted carrier can have Logisti installed, and
 * both lifecycle hooks fire on the same order transition; the issuer claims
 * the order with a conditional update on `status` / `tripId` here, so exactly
 * one caller can proceed and the loser is a no-op rather than a second
 * government document. See `LogistiWaybillService.claimOrder`.
 *
 * ## Not the same thing as Hero's own waybill PDF
 *
 * `Order.waybill` describes Hero's internal branded PDF, which exists
 * regardless of any app. The government document is a separate artifact with
 * legal standing, so it gets its own top-level field and its own `image` —
 * neither ever overwrites the other. The shape extends
 * {@link orderWaybillSchema} because the two genuinely share a spine: a
 * status, a generated document, a timestamp, and a last error.
 *
 * ## No `.default()` on any field here
 *
 * Every field is `.optional()`, never `.default()`. `DBCrud.updateOne`
 * zod-parses `$set` against the Order's partial schema, and a partial schema is
 * shallow — so a write of `{ waybillBayan: { status } }` re-parses this whole
 * object and any `.default()` would be injected and flattened into the update,
 * silently resetting `attempts` and `waybillIds` on every state change.
 */
export const orderWaybillBayanSchema = orderWaybillSchema.extend({
  /** Overrides the base `generating | ready | failed` with Bayan's lifecycle. */
  status: waybillBayanStatusSchema.optional(),

  /**
   * Bayan trip id — what a TGA inspector asks for.
   *
   * Stored as a **string** even though Bayan returns a number: it is an
   * identifier, not a quantity. Nothing adds or compares it, everything
   * displays it, and the operational need is to *find an order by the
   * number a driver read off a document* — which means text search and
   * partial matching, neither of which works against a numeric field.
   *
   * Documents written before this change still hold a number. Reads are
   * not zod-parsed, so both shapes reach runtime: coerce with
   * `String(tripId)` when displaying and `Number(tripId)` when calling
   * Bayan. Deliberately not migrated — the two coercions are cheaper and
   * safer than rewriting live government-document references.
   */
  tripId: z.string().optional(),

  /**
   * Bayan waybill ids under the trip.
   *
   * v1 issues one trip carrying one waybill per Hero order. This stays an
   * array so a multi-destination order mapping to several waybills on one
   * trip is a mapper change, not a schema change.
   *
   * `.optional()` rather than `.default([])` deliberately — see the note on
   * {@link orderWaybillBayanSchema} about partial writes.
   */
  waybillIds: z.array(z.number()).optional(),

  /**
   * The Org whose Bayan account issued this — the "issuing org".
   *
   * Not necessarily the order's buyer or seller: on a brokered load the
   * broker issues while a different carrier executes. Needed as a party (not
   * just a name) because close, cancel, and PDF fetch all have to reload that
   * Org's install to make the call.
   */
  issuingOrg: partySchema.optional(),

  /** `AppInstalled` `_id` hex string used for the call. Absent on platform filing. */
  installedId: idSchema.optional(),

  /**
   * Whether Hero's own Bayan licence filed this, rather than an Org install.
   *
   * A trip belongs to the `client_id` that created it, so close, cancel, and
   * PDF fetch have to go back through the same account. Without this flag an
   * Org that connects its own account after a platform-filed document would
   * have its close call refused by Bayan: `issuingOrg` names the Org the
   * document was filed *for*, which is the right audit anchor but no longer
   * identifies the credential once that Org has one of its own.
   */
  issuedByPlatform: z.boolean().optional(),

  /** When the trip was successfully created in Bayan. */
  issuedAt: dateSchema.optional(),

  /**
   * Where the PDF was last delivered — the WhatsApp number and the thread.
   *
   * A replacement filed after an edit goes to the same place, whichever
   * surface made the edit. Kept across a reissue on purpose: it describes
   * the recipient, not the voided trip.
   */
  deliveredTo: z
    .object({
      phone: z.string(),
      conversationId: z.string().optional(),
    })
    .optional(),

  /**
   * The chat this document is owed to — written when a chat asks for it,
   * before Bayan is called.
   *
   * `deliveredTo` only exists once a PDF has left, so whatever files or
   * archives the document later (a lifecycle hook that won the claim, the
   * retry sweep after an outage, a replacement after an edit, the PDF
   * sweep after Bayan rendered late) reads this to know where it goes. A
   * target with no matching `deliveredTo` is a document still owed.
   */
  deliveryTarget: z
    .object({
      phone: z.string(),
      conversationId: z.string().optional(),
    })
    .optional(),

  /** PDF-sweep passes that found this owed document still undelivered. */
  deliverySweeps: z.number().int().min(0).optional(),

  /** When the waybill was closed (order delivered). */
  closedAt: dateSchema.optional(),

  /** When the waybill was cancelled, and with which Bayan reason id. */
  canceledAt: dateSchema.optional(),
  cancelReasonId: z.number().optional(),

  /**
   * When the current attempt claimed this order.
   *
   * Doubles as a lease: a process that crashes mid-call leaves `pending`
   * behind, and without an expiry the order could never be retried. A claim
   * older than the lease window is reclaimable.
   */
  claimedAt: dateSchema.optional(),

  /** Stable failure code (e.g. `missing_carrier_moi`) for localized copy. */
  errorCode: z.string().optional(),

  /**
   * Why the last attempt could not even be sent — missing Hero-side data.
   *
   * Distinct from the inherited `error`, which is Bayan's own refusal.
   * Blockers are the user's to fix; `error` usually is not.
   */
  blockers: z.array(waybillBlockerSchema).optional(),

  /**
   * How many send attempts have been made.
   *
   * Read by the retry cron to stop hammering a permanently broken order.
   */
  attempts: z.number().int().min(0).optional(),

  /**
   * Plate decomposition actually sent to Bayan, kept for auditing.
   *
   * Retained because the Arabic right/middle/left ordering is the riskiest
   * assumption in the mapper — if a plate turns out transposed, this is the
   * record that proves which way round Hero sent it.
   */
  sentPlate: z
    .object({
      rightLetter: z.string(),
      middleLetter: z.string(),
      leftLetter: z.string(),
      number: z.string(),
    })
    .optional(),

  /**
   * The driver identity actually sent to Bayan (create or the last
   * accepted update), kept next to `sentPlate` for the same reason and
   * one more: the post-issuance "swap" hook compares the order's current
   * assignment against THIS to decide whether anything on the document
   * changed. Without it the assignment write that issuance itself
   * produced fired an `update_trip` with identical values ~3s after
   * `create_trip`, which ELM refused («trip not saved») — a phantom
   * failure on a perfectly good document.
   */
  sentDriver: z
    .object({
      identityNumber: z.string(),
      issueNumber: z.number().int().optional(),
    })
    .optional(),
});

export type OrderWaybillBayan = z.infer<typeof orderWaybillBayanSchema>;
